<template>
  <Section :settings="{ ...settings, alignment: 'center', title: sectionTitle }">
    <div class="asset-library-section">
      <div class="container-fluid">
        <!-- Search Bar -->
        <div class="row mt-5">
          <div class="col-md-8 offset-md-2">
            <div class="d-flex justify-content-between">
              <div class="position-relative flex-grow-1">
                <div class="search-icon text-muted">
                  <i class="bi bi-search" />
                </div>
                <input
                  v-model="searchKeyword"
                  placeholder="Search Customer Stories"
                  class="search-input form-control border-2"
                  @input="searchContentAssets"
                />
                <div
                  v-if="searchKeyword.length > 0"
                  class="search-clear-icon position-absolute text-muted"
                  @click="clearSearchKeyword"
                >
                  <i class="bi bi-x" />
                </div>
              </div>
              <button
                v-if="!lockFilters"
                class="btn btn-primary ml-2"
                @click="filtersCollapse.toggle()"
              >
                <i class="bi bi-filter" />
              </button>
            </div>
          </div>
        </div>

        <!-- Filters -->
        <div class="row mt-2 asset-filters-row">
          <div class="col-12">
            <UeCollapse :visible="lockFilters ? true : filtersCollapse.visible.value" class="mt-2">
              <div class="asset-filters">
                <UeSelect v-model="type" :options="assetFilters" />
                <UeSelect
                  v-model="sectorId"
                  :options="sectorFilters"
                  :disabled="!sectorFilters || sectorFilters.length === 0"
                />
                <UeSelect
                  v-model="sizeGroupId"
                  :options="sizeGroupFilters"
                  :disabled="!sizeGroupFilters || sizeGroupFilters.length === 0"
                />
                <UeSelect
                  v-model="seniorityId"
                  :options="seniorityFilters"
                  :disabled="!seniorityFilters || seniorityFilters.length === 0"
                />
                <UeSelect
                  v-model="recipientRoleId"
                  :options="recipientRoleFilters"
                  :disabled="!recipientRoleFilters || recipientRoleFilters.length === 0"
                />
              </div>
            </UeCollapse>
          </div>
        </div>

        <!-- Assets Grid -->
        <div class="row mt-5" v-if="initialized && contentAssets && contentAssets.length > 0">
          <div class="col-12">
            <UeMasonry :options="{ columns: 3, margin: 10 }">
              <div v-for="asset in contentAssets" :key="asset.id" class="asset-card mb-4">
                <a :href="assetUrl(asset)" target="_blank" class="text-decoration-none">
                  <TestimonialBasic
                    v-if="asset.type === 'RenderableTestimonial'"
                    :renderable-testimonial="asset"
                    :account="account"
                    renderAsList
                  />
                  <BarChartBasic
                    v-else-if="asset.type === 'ChartAsset'"
                    :content-asset="theAsset(asset)"
                    :account="account"
                    renderAsList
                  />
                  <StatAsset
                    v-else-if="asset.type === 'StatAsset'"
                    :content-asset="theAsset(asset)"
                    :account="account"
                    renderAsList
                  />
                  <Microsite v-else-if="asset.type === 'AssetList'" :asset_list="asset" />
                  <SpotlightPreview
                    v-else-if="['CustomerSpotlight', 'SurveySpotlightAsset'].includes(asset.type)"
                    :content_asset="theAsset(asset)"
                    :customer_spotlight="asset"
                    :account="account"
                  />
                  <SpotlightPreview
                    v-else-if="asset.type === 'CaseStudy' && caseStudiesEnabled"
                    :content_asset="theAsset(asset)"
                    :account="account"
                  />
                  <MatrixxChartBasic
                    v-else-if="asset.type === 'MatrixxChart'"
                    class="as_list"
                    :matrixxChart="theAsset(asset)"
                    :account="account"
                    renderAsList
                  />
                  <VideoLinkAsset
                    v-else-if="asset.type === 'VideoLinkAsset'"
                    :video-link-asset="theAsset(asset)"
                    :account="account"
                    renderAsList
                  />
                  <FileAsset
                    v-else-if="asset.type === 'FileAsset'"
                    class="as_list"
                    :file-asset="theAsset(asset)"
                    :account="account"
                  />
                </a>
              </div>
            </UeMasonry>
          </div>
        </div>

        <!-- No Assets Message -->
        <div v-else-if="initialized" class="row mt-4">
          <div class="col-12 text-center">
            <div class="no-assets-message py-5">
              <h5>No Content Assets Yet</h5>
              <p class="text-muted">
                We're currently collecting survey results and will have content assets ready soon.
              </p>
            </div>
          </div>
        </div>

        <div v-else-if="!initialized" class="row mt-4">
          <div class="col-12 text-center">
            <ue-spinner />
          </div>
        </div>

        <!-- Pagination -->
        <div v-if="contentAssets && contentAssets.length > 0" class="row mt-4">
          <div class="col-12 d-flex justify-content-center">
            <UePagination v-model="page" :records="totalCount" :per-page="25" />
          </div>
        </div>
      </div>
    </div>
  </Section>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted } from 'vue'
import axios from '@lib/apiClient'
import _ from 'lodash'
import { excludeFalseyValues } from '@lib/collections'
import { reportError } from '@lib/errorHandler'
import { Account } from '@/types/Account'
import Section from '@components/customer_page/sections/Section.vue'
import TestimonialBasic from '@/components/TestimonialBasic.vue'
import MatrixxChartBasic from '@/components/MatrixxChartBasic.vue'
import BarChartBasic from '@/components/BarChartBasic.vue'
import StatAsset from '@/components/StatBasic.vue'
import Microsite from '@/app/components/Microsite.vue'
import SpotlightPreview from '@/components/SpotlightPreview.vue'
import VideoLinkAsset from '@/components/VideoLinkAsset.vue'
import FileAsset from '@/components/FileAsset.vue'
import useCollapse from '@/composables/useCollapse'
import { SectionSettings } from '../types'
import { getColorById } from '../utils'

interface Filter {
  code: string
  label: string
}

interface Props {
  account: Account
  editable?: boolean
  settings: SectionSettings
}

const props = withDefaults(defineProps<Props>(), {
  editable: false,
})

const FILTERS_DEFAULT: Record<string, Filter> = {
  TYPES: { code: '', label: 'All Asset Types' },
  SECTORS: { code: '', label: 'All Sectors' },
  SIZE_GROUPS: { code: '', label: 'All Size Groups' },
  SENIORITIES: { code: '', label: 'All Seniorities' },
  RECIPIENT_ROLES: { code: '', label: 'All Recipient Roles' },
}

const filtersCollapse = useCollapse(false)
const mediaQuery = window.matchMedia('(max-width: 900px)')

// Reactive data
const searchKeyword = ref('')
const sectorFilters = ref<Filter[]>([])
const sizeGroupFilters = ref<Filter[]>([])
const seniorityFilters = ref<Filter[]>([])
const recipientRoleFilters = ref<Filter[]>([])
const sectorId = ref(FILTERS_DEFAULT.SECTORS)
const sizeGroupId = ref(FILTERS_DEFAULT.SIZE_GROUPS)
const seniorityId = ref(FILTERS_DEFAULT.SENIORITIES)
const recipientRoleId = ref(FILTERS_DEFAULT.RECIPIENT_ROLES)
const page = ref(1)
const contentAssets = ref<any[]>([])
const totalCount = ref(0)
const initialized = ref(false)
const type = ref(FILTERS_DEFAULT.TYPES)
const lockFilters = ref(false)

const typeFilter = ref([
  FILTERS_DEFAULT.TYPES,
  { code: 'ChartAsset', label: 'Charts' },
  { code: 'CustomerSpotlight', label: 'Customer Spotlights' },
  { code: 'RenderableTestimonial', label: 'Testimonials' },
  { code: 'StatAsset', label: 'Stats' },
  { code: 'SpotlightAsset', label: 'Reports' },
  { code: 'MatrixxChart', label: 'Matrix Charts' },
  { code: 'AssetList', label: 'Microsites' },
  { code: 'VideoLinkAsset', label: 'Video Link Assets' },
  { code: 'FileAsset', label: 'File Assets' },
])

// Computed properties
const sectionTitle = computed(() => {
  return props.settings.title || `Explore ${props.account?.name}'s customer success stories`
})

const textColor = computed(() => {
  return getColorById(props.settings.design_settings?.colors, 'text_color') || 'gray-900'
})

const caseStudiesEnabled = computed(() => {
  return props.account?.flipper_features?.includes('case_studies') || false
})

const assetFilters = computed(() => {
  let filters = [...typeFilter.value]
  if (caseStudiesEnabled.value) {
    filters.push({ code: 'CaseStudy', label: 'Case Studies' })
  }
  return filters
})

const assetsUrl = computed(() => {
  return `/api/accounts/${props.account.id}/content_assets.json`
})

const filterUrl = computed(() => {
  return `/api/accounts/${props.account.id}/asset_filters.json`
})

// Methods
const mapFilters = (defaultFilter: Filter, filters: Array<unknown>): Filter[] => {
  const result = filters.map((filter: { id: string; name: string }) => ({
    code: filter.id,
    label: filter.name,
  }))
  return [defaultFilter, ...result]
}

const getContentAssets = async (): Promise<void> => {
  if (!props.account?.id) return

  const query = {
    page: page.value,
    sort_by: 'updated_at',
    sort_dir: 'desc',
    q: searchKeyword.value,
    type: type.value.code,
    size_group: sizeGroupId.value.code,
    sector_id: sectorId.value.code,
    seniority: seniorityId.value.code,
    recipient_role_id: recipientRoleId.value.code,
  }

  try {
    const [assetsResponse, filtersResponse] = await Promise.all([
      axios.get(assetsUrl.value, { params: excludeFalseyValues(query) }),
      axios.get(filterUrl.value, { params: excludeFalseyValues(query) }),
    ])

    contentAssets.value = assetsResponse.data.content_assets
    totalCount.value = assetsResponse.data.total_count
    sizeGroupFilters.value = mapFilters(
      FILTERS_DEFAULT.SIZE_GROUPS,
      filtersResponse.data.size_group_filters,
    )
    sectorFilters.value = mapFilters(FILTERS_DEFAULT.SECTORS, filtersResponse.data.sector_filters)
    seniorityFilters.value = mapFilters(
      FILTERS_DEFAULT.SENIORITIES,
      filtersResponse.data.seniority_filters,
    )
    recipientRoleFilters.value = mapFilters(
      FILTERS_DEFAULT.RECIPIENT_ROLES,
      filtersResponse.data.recipient_role_filters,
    )
    initialized.value = true
  } catch (error) {
    reportError(error, 'getAssets', 'AssetLibrarySection')
    initialized.value = true
  }
}

const debouncedSearch = _.debounce(() => {
  page.value = 1
  getContentAssets()
}, 500)

const searchContentAssets = (): void => {
  debouncedSearch()
}

const clearSearchKeyword = (): void => {
  searchKeyword.value = ''
  getContentAssets()
}

const theAsset = (asset: any) => {
  return Object.assign(asset, { account: props.account })
}

const assetUrl = (asset: any) => {
  return asset.type === 'AssetList' ? `/sites/${asset.slug}` : `/assets/${asset.identifier}`
}

const debouncedMediaQueryChange = _.debounce((event: MediaQueryListEvent | MediaQueryList) => {
  if (event.matches) {
    lockFilters.value = false
  } else {
    lockFilters.value = true
  }
}, 200)

// Watchers
watch(page, () => {
  getContentAssets()
})

watch(type, () => {
  getContentAssets()
})

watch(sizeGroupId, () => {
  getContentAssets()
})

watch(sectorId, () => {
  getContentAssets()
})

watch(seniorityId, () => {
  getContentAssets()
})

watch(recipientRoleId, () => {
  getContentAssets()
})

onMounted(() => {
  // This is used to lock the filters toggle when the screen is smaller than 980px.
  mediaQuery.addEventListener('change', debouncedMediaQueryChange)
  // Execute immediately on mount to set initial state (not debounced for initial load)
  debouncedMediaQueryChange(mediaQuery)

  getContentAssets()
})
</script>

<style scoped lang="sass">
.heading
  color: v-bind('textColor')

.search-icon
  position: absolute
  left: 1rem
  top: 50%
  transform: translateY(-50%)
  z-index: 10

.search-input
  height: 3rem
  padding-left: 2.5rem !important

  &:focus
    border-color: #6f42c1
    box-shadow: 0 0 0 0.2rem rgba(111, 66, 193, 0.25)

.search-clear-icon
  position: absolute
  cursor: pointer
  z-index: 10
  right: 1rem
  top: 50%
  transform: translateY(-50%)

.asset-filters
  display: flex
  flex-wrap: wrap
  gap: 1rem
  justify-content: center

  @media (max-width: 768px)
    flex-direction: column
    align-items: center
</style>
